import { Injectable } from '@angular/core';
import { ArticleConstants } from '@article/constants/article-constants';
import { CaeProjectHelper } from '@project/processor/cae/cae-project-helper';
import { lastValueFrom } from 'rxjs';
import { ArticleTypeIds } from '@article/constants/article-type-ids';
import { Log } from '@shared/debug/log';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleManager } from '@article/manager/article.manager';
import { MountingRailArticlePostParseProcessor } from '@project/components/article/processor/postparse/mounting-rail-post-parse.processor';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { MountingRailCreationModel } from '../../../wizard/service/mounting-rail-creation.model';
import { MountingRailCreationService } from '../../../wizard/service/mounting-rail-creation.service';
import { ExchangeMountingRailModelProcessor } from '@project/cae/exchange-mounting-rail-model.processor';
import { ProjectModel } from '@project/model/project.model';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ArticleVO } from '@article/model/vo/article.vo';
import { CaeImportHelperService } from './cae-import-helper.service';
import { CaeImportErrorCollector } from '../cae-import-error-collector';
import { UnitConverter } from '@3d-scene/common/util/unit-converter';
import { CaeRailsData } from './cae-rails-data';

@Injectable({
  providedIn: 'root',
})
export class CaeImportMountingRailService {

  constructor(
    private articleDataService: ArticleDataService,
    private caeProjectHelper: CaeProjectHelper,
    private articleManager: ArticleManager,
    private containerService: ContainerService,
    private mountingRailCreationService: MountingRailCreationService,
    private exchangeMountingRailModelProcessor: ExchangeMountingRailModelProcessor,
    private projectModel: ProjectModel,
    private caeImportHelperService: CaeImportHelperService,
  ) {
  }

  public async addMountingRail(caeRailsData: CaeRailsData, caeImportErrorCollector: CaeImportErrorCollector): Promise<void> {

    const caeRail = caeRailsData.currentRails[0];
    if (!caeRail) {
      return;
    }

    caeImportErrorCollector.processRail(caeRail);

    const defaultRailNo = await this.loadDefaultCarrierRail();
    const railNo = this.caeProjectHelper.getArticleNumberCleared(caeRail.articleNo);

    if ((!railNo && !defaultRailNo) || railNo === ArticleConstants.FREE_MOUNTING_ARTICLE_NO) {
      return;
    }

    let newRail = await this.loadNewRail(railNo, defaultRailNo);
    if (!newRail) {
      return;
    }

    if (newRail.type.id != ArticleTypeIds.MOUNTING_RAIL) {
      caeImportErrorCollector.addErrorMessage('caeImport.carrierrail.error.not_a_mounting_rail', [ railNo ]);
      return;
    }

    const railLength: number = this.caeImportHelperService.millimetersToAway3DUnits(caeRail.size.width);
    const maxRailLength: number = MountingRailArticlePostParseProcessor.getRailMaxLength(newRail);

    const railContainer = this.containerService.findArticleContainerInGlobal(ProjectUtils.getFirstArticleUsage(this.projectModel.currentProject).id);

    const mountingRailCreationModel = new MountingRailCreationModel();
    await this.mountingRailCreationService.setMountingRailArticle(railContainer.article, mountingRailCreationModel);
    this.mountingRailCreationService.setExistingMountingRail(railContainer, mountingRailCreationModel);
    const projectLength = Math.ceil(mountingRailCreationModel.usedMountingRailLength / 10) * 10;

    let newRailLength = railLength;
    if (railLength < projectLength) {
      newRailLength = projectLength + 200;
      const newRailInMM = UnitConverter.fromTo('3d', 'mm', newRailLength);
      caeImportErrorCollector.addErrorMessage('caeImport.carrierrail.error.wanted_rail_Length_To_short', [ newRailInMM.toString() ]);
    }
    if (projectLength > maxRailLength) {
      caeImportErrorCollector.addErrorMessage('caeImport.carrierrail.error.max_rail_Length_To_short');
      return;
    }

    try {
      const unconnectableArticles = await this.exchangeMountingRailModelProcessor.replaceSelectedCarrierRail(railContainer, newRail, newRailLength, mountingRailCreationModel);
      if (unconnectableArticles.length > 0) {
        const articleNos: string = unconnectableArticles.map(a => a.articleNo).join(', ');
        caeImportErrorCollector.addErrorMessage('caeImport.carrierrail.error.rail_not_compatible_to_articles', [ articleNos ]);
      }
    } catch (error) {
      Log.error('Could not replace selected carrier rail: ', error);
    }
  }

  private async loadDefaultCarrierRail(): Promise<string | undefined> {
    const favoriteArticles = await lastValueFrom(this.articleDataService.getFavoriteArticles([ ArticleTypeIds.MOUNTING_RAIL ]));
    if (Log.assertExists(favoriteArticles)) {
      const rails = favoriteArticles[0].favoriteArticles;
      rails.sort((a, b) => a.sortOrder - b.sortOrder);
      return rails[0].articleNumber;
    }
  }

  private async loadNewRail(railNo: string, defaultRailNo: string | undefined): Promise<ArticleVO | undefined> {
    const railArticle = await lastValueFrom(this.articleManager.getArticleByArticleNo(railNo));
    if (railArticle) {
      return railArticle;
    }
    if (!defaultRailNo) {
      return undefined;
    }
    const defaultRailArticle = await lastValueFrom(this.articleManager.getArticleByArticleNo(defaultRailNo));
    return defaultRailArticle;
  }
}
